import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardFooter, Container, Row, Col, FormGroup, Input, Spinner, Button, Pagination, Modal, ModalHeader, ModalBody, ModalFooter, Label, ButtonGroup, Form } from 'reactstrap';
import { FaEdit, FaPlus, FaPrint, FaShareAlt } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import ReactPaginate from 'react-paginate';
import { apiURL1 } from '../../config';
import Header from "components/Headers/Header.js";
import Footer from "components/Footers/Footer";
import CustomTable from "components/CustomTable";

const StudentMonthlyFee = () => {
    const [feesData, setFeesData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterMonth, setFilterMonth] = useState(dayjs().format('MM')); // Default to current month
    const [filterYear, setFilterYear] = useState(dayjs().year().toString()); // Default to current year
    const [filterStudentName, setFilterStudentName] = useState('');
    const [filterRegNo, setFilterRegNo] = useState('');
    const [filterClass, setFilterClass] = useState('');
    const [classes, setClasses] = useState([]);
    const [summary, setSummary] = useState({ paid: 0, pending: 0, partPaid: 0, total: 0 });
    const [madrasaInfo, setMadrasaInfo] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedFee, setSelectedFee] = useState(null);
    const [newFee, setNewFee] = useState({
        paidDate: '',
        amount: '',
        month: '',
        year: '',
        isFullPay: true,
        notes: '',
        pending: 0,
        isBank: false
    });
    const [editFee, setEditFee] = useState(null);
    const [errors, setErrors] = useState({});
    const token = localStorage.getItem('token');

    const months = [
        { label: 'All Months', value: '' },
        { label: 'January', value: '01' },
        { label: 'February', value: '02' },
        { label: 'March', value: '03' },
        { label: 'April', value: '04' },
        { label: 'May', value: '05' },
        { label: 'June', value: '06' },
        { label: 'July', value: '07' },
        { label: 'August', value: '08' },
        { label: 'September', value: '09' },
        { label: 'October', value: '10' },
        { label: 'November', value: '11' },
        { label: 'December', value: '12' },
    ];

    const years = Array.from({ length: 2030 - 2025 + 1 }, (_, i) => 2025 + i).map(year => ({ label: year.toString(), value: year.toString() }));

    const fetchFees = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${apiURL1}/superadmin/studentsFee?page=${page}&limit=10`;
            if (filterMonth) url += `&month=${filterMonth}`;
            if (filterYear) url += `&year=${filterYear}`;
            if (filterStudentName) url += `&studentName=${filterStudentName}`;
            if (filterRegNo) url += `&registrationNumber=${filterRegNo}`;
            if (filterClass) url += `&classId=${filterClass}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setFeesData(response.data.fees || []);
            setTotalPages(response.data.totalPages || 1);
            setSummary(response.data.summary || { paid: 0, pending: 0, partPaid: 0, total: 0 });
        } catch (error) {
            console.error('Error fetching student fees:', error.response?.data || error);
            toast.error(error.response?.data?.message || 'Error fetching student fees!');
        } finally {
            setLoading(false);
        }
    }, [filterMonth, filterYear, filterStudentName, filterRegNo, filterClass, page, token]);

    const fetchClasses = useCallback(async () => {
        if (loading) return; // Prevent concurrent requests
        try {
            const response = await axios.get(`${apiURL1}/superadmin/getClassName`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setClasses(response.data || []);
        } catch (error) {
            console.error('Error fetching classes:', error);
            toast.error('Failed to fetch classes!');
        }
    }, [token]);

    const fetchMadrasaInfo = async () => {
        if (loading) return; // Prevent concurrent requests
        try {
            const response = await axios.get(`${apiURL1}/superadmin/school-settings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = response.data;
            if (data && data.length > 0) {
                setMadrasaInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching madrasa info:', error);
        }
    };

    const validateFee = (fee) => {
        const newErrors = {};
        if (!fee.paidDate) newErrors.paidDate = 'Paid date is required';
        if (!fee.amount || Number(fee.amount) <= 0) newErrors.amount = 'Amount must be a positive number';
        if (!fee.month) newErrors.month = 'Month is required';
        else if (!Number.isInteger(Number(fee.month)) || Number(fee.month) < 1 || Number(fee.month) > 12) {
            newErrors.month = 'Month must be between 1 and 12';
        }
        if (!fee.year) newErrors.year = 'Year is required';
        else if (!Number.isInteger(Number(fee.year)) || Number(fee.year) < 2000) {
            newErrors.year = 'Year must be >= 2000';
        }
        if (fee.isFullPay === undefined) newErrors.isFullPay = 'Full pay status is required';
        if (fee.isBank === undefined) newErrors.isBank = 'Payment method is required';
        if (!fee.isFullPay && (!fee.pending || Number(fee.pending) <= 0)) {
            newErrors.pending = 'Pending amount must be a positive number';
        }
        return newErrors;
    };

    // Dynamic validation for Add modal
    useEffect(() => {
        if (modalOpen) {
            const validationErrors = validateFee(newFee);
            setErrors(validationErrors);
        }
    }, [newFee, modalOpen]);

    // Dynamic validation for Edit modal
    useEffect(() => {
        if (editModalOpen && editFee) {
            const validationErrors = validateFee(editFee);
            setErrors(validationErrors);
        }
    }, [editFee, editModalOpen]);

    const handleAddFee = async (e, studentId) => {
        e.preventDefault();
        const validationErrors = validateFee(newFee);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error('Please fill all required fields correctly');
            return;
        }
        try {
            const response = await axios.post(`${apiURL1}/superadmin/students/${studentId}/fees`, {
                ...newFee,
                month: Number(newFee.month),
                year: Number(newFee.year),
                amount: Number(newFee.amount),
                pending: Number(newFee.pending)
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });
            if (response.status === 200 || response.status === 201) {
                toast.success('Fee added successfully!');
                fetchFees();
                setNewFee({
                    paidDate: '',
                    amount: '',
                    month: '',
                    year: '',
                    isFullPay: true,
                    notes: '',
                    pending: 0,
                    isBank: false
                });
                setModalOpen(false);
                setErrors({});
            } else {
                toast.error(response.data.message || 'Error adding fee!');
            }
        } catch (error) {
            console.error('Error adding fee:', error);
            toast.error(error.response?.data?.message || 'Error adding fee!');
        }
    };

    const handleEditFee = async (e) => {
        e.preventDefault();
        const validationErrors = validateFee(editFee);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error('Please fill all required fields correctly');
            return;
        }
        try {
            const response = await axios.put(`${apiURL1}/superadmin/students/${selectedFee.studentId}/fees/${editFee._id}`, {
                ...editFee,
                month: Number(editFee.month),
                year: Number(editFee.year),
                amount: Number(editFee.amount),
                pending: Number(editFee.pending)
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });
            if (response.status === 200) {
                toast.success('Fee updated successfully!');
                fetchFees();
                setEditFee(null);
                setEditModalOpen(false);
                setErrors({});
            } else {
                toast.error(response.data.message || 'Error updating fee!');
            }
        } catch (error) {
            console.error('Error updating fee:', error);
            toast.error(error.response?.data?.message || 'Error updating fee!');
        }
    };

    const openAddModal = (fee) => {
        setSelectedFee(fee);
        setNewFee({
            paidDate: '',
            amount: '',
            month: filterMonth && filterMonth !== '' ? filterMonth : dayjs().format('MM'),
            year: filterYear && filterYear !== '' ? filterYear : dayjs().year().toString(),
            isFullPay: true,
            notes: '',
            pending: 0,
            isBank: false
        });
        setModalOpen(true);
        setErrors({});
    };

    const openEditModal = (fee) => {
        setSelectedFee(fee);
        setEditFee({
            ...fee,
            paidDate: fee.paidDate ? fee.paidDate.split('T')[0] : '',
            month: fee.month || '',
            year: fee.year || ''
        });
        setEditModalOpen(true);
        setErrors({});
    };

    useEffect(() => {
        // Initial load for settings
        fetchClasses();
        fetchMadrasaInfo();
    }, []); // Run only once on mount

    // Debounce filter changes
    const debounceTimeout = useRef(null);
    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            fetchFees();
        }, 300); // 300ms debounce
        return () => clearTimeout(debounceTimeout.current);
    }, [filterMonth, filterYear, filterStudentName, filterRegNo, filterClass, page, fetchFees]);

    const formatDate = (isoDate) => {
        if (!isoDate) return '-';
        return dayjs(isoDate).format('DD-MM-YYYY');
    };

    const handleFilterChange = (filterType, value) => {
        switch (filterType) {
            case 'studentName':
                setFilterStudentName(value);
                break;
            case 'regNo':
                setFilterRegNo(value);
                break;
            case 'class':
                setFilterClass(value);
                break;
            case 'month':
                setFilterMonth(value);
                break;
            case 'year':
                setFilterYear(value);
                break;
            default:
                break;
        }
        setPage(1);
    };

    const handlePageChange = (selectedPage) => {
        setPage(selectedPage.selected + 1);
    };

    const buildAllFeesPdfUrl = () => {
        let url = `${apiURL1}/superadmin/studentsFee/pdf?month=${filterMonth}&year=${filterYear}`;
        if (filterStudentName) url += `&studentName=${encodeURIComponent(filterStudentName)}`;
        if (filterRegNo) url += `&registrationNumber=${encodeURIComponent(filterRegNo)}`;
        if (filterClass) url += `&classId=${filterClass}`;
        return url;
    };

    const buildIndividualFeePdfUrl = (fee) => {
        let url = `${apiURL1}/superadmin/studentsFee/pdf/individual?studentId=${fee.studentId}&month=${fee.month || filterMonth}&year=${fee.year || filterYear}`;
        if (fee._id) url += `&feeId=${fee._id}`;
        return url;
    };

    const fetchPdfBlob = async (url) => {
        const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
        });
        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('application/json')) {
            const text = await response.data.text();
            const err = JSON.parse(text);
            throw new Error(err.message || 'Failed to generate PDF');
        }
        return response.data;
    };

    const openPdfBlob = (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    };

    const sharePdfBlob = async (blob, filename) => {
        const file = new File([blob], filename, { type: 'application/pdf' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: filename, text: 'Monthly Fee PDF' });
            return;
        }
        // Fallback: download PDF so user can share manually
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        toast.info('PDF downloaded. Share it from your device.');
    };

    const handlePrintAll = async () => {
        if (!filterMonth || !filterYear) {
            toast.error('Please select both month and year.');
            return;
        }
        setLoading(true);
        try {
            const blob = await fetchPdfBlob(buildAllFeesPdfUrl());
            openPdfBlob(blob);
        } catch (error) {
            console.error('Error printing all fees:', error);
            toast.error(error.response?.data?.message || 'Error generating PDF!');
        } finally {
            setLoading(false);
        }
    };

    const handleShareAll = async () => {
        if (!filterMonth || !filterYear) {
            toast.error('Please select both month and year.');
            return;
        }
        setLoading(true);
        try {
            const blob = await fetchPdfBlob(buildAllFeesPdfUrl());
            await sharePdfBlob(blob, `Fee_Report_${filterYear}_${filterMonth}.pdf`);
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Error sharing all fees:', error);
            toast.error(error.response?.data?.message || 'Error sharing PDF!');
        } finally {
            setLoading(false);
        }
    };

    const handlePrintIndividual = async (fee) => {
        try {
            const blob = await fetchPdfBlob(buildIndividualFeePdfUrl(fee));
            openPdfBlob(blob);
        } catch (error) {
            console.error('Error printing fee:', error);
            toast.error(error.response?.data?.message || 'Error generating PDF!');
        }
    };

    const handleShareIndividual = async (fee) => {
        try {
            const blob = await fetchPdfBlob(buildIndividualFeePdfUrl(fee));
            const name = (fee.fullName || 'student').replace(/\s+/g, '_');
            await sharePdfBlob(blob, `Fee_${name}_${fee.year || filterYear}_${fee.month || filterMonth}.pdf`);
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Error sharing fee:', error);
            toast.error(error.response?.data?.message || 'Error sharing PDF!');
        }
    };

    const columns = [
        "Student Name",
        "Class Name",
        "Reg No",
        "Email",
        "Paid Date",
        "Amount",
        "Status",
        "Notes",
        "Actions"
    ];

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <Row>
                    <div className="col">
                        <Card className="shadow">
                            <CardHeader className="border-0 px-2 px-md-3">
                                <div className="d-flex flex-wrap align-items-end justify-content-between" style={{ gap: '8px' }}>
                                    <h3 className="mb-0 mr-2">Student Fees</h3>
                                    <div className="d-flex flex-wrap align-items-end" style={{ gap: '8px' }}>
                                        <FormGroup className="mb-0">
                                            <Input
                                                bsSize="sm"
                                                type="text"
                                                placeholder="Student Name"
                                                value={filterStudentName}
                                                onChange={(e) => handleFilterChange('studentName', e.target.value)}
                                                style={{ minWidth: 130 }}
                                            />
                                        </FormGroup>
                                        <FormGroup className="mb-0">
                                            <Input
                                                bsSize="sm"
                                                type="text"
                                                placeholder="Reg No"
                                                value={filterRegNo}
                                                onChange={(e) => handleFilterChange('regNo', e.target.value)}
                                                style={{ minWidth: 100 }}
                                            />
                                        </FormGroup>
                                        <FormGroup className="mb-0">
                                            <Input
                                                bsSize="sm"
                                                type="select"
                                                value={filterClass}
                                                onChange={(e) => handleFilterChange('class', e.target.value)}
                                                style={{ minWidth: 120 }}
                                            >
                                                <option value="">All Classes</option>
                                                {classes.map(cls => (
                                                    <option key={cls._id} value={cls._id}>
                                                        {cls.className}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                        <FormGroup className="mb-0">
                                            <Input
                                                bsSize="sm"
                                                type="select"
                                                value={filterMonth}
                                                onChange={(e) => handleFilterChange('month', e.target.value)}
                                                style={{ minWidth: 110 }}
                                            >
                                                {months.map(month => (
                                                    <option key={month.value} value={month.value}>
                                                        {month.label}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                        <FormGroup className="mb-0">
                                            <Input
                                                bsSize="sm"
                                                type="select"
                                                value={filterYear}
                                                onChange={(e) => handleFilterChange('year', e.target.value)}
                                                style={{ minWidth: 90 }}
                                            >
                                                {years.map(year => (
                                                    <option key={year.value} value={year.value}>
                                                        {year.label}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                        <ButtonGroup size="sm">
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={handlePrintAll}
                                                disabled={loading}
                                                className="d-inline-flex align-items-center"
                                            >
                                                {loading ? <Spinner size="sm" /> : <><FaPrint className="mr-1" /> Print All</>}
                                            </Button>
                                            <Button
                                                color="info"
                                                size="sm"
                                                onClick={handleShareAll}
                                                disabled={loading}
                                                className="d-inline-flex align-items-center"
                                            >
                                                <FaShareAlt className="mr-1" /> Share All
                                            </Button>
                                        </ButtonGroup>
                                    </div>
                                </div>
                            </CardHeader>
                            {loading ? (
                                <Spinner size="lg" className="my-5 mx-auto" />
                            ) : (
                                <CustomTable
                                    columns={columns}
                                    rows={feesData.length > 0 ? feesData.flatMap((classGroup) =>
                                        classGroup.fees.map((fee) => ({
                                            "Student Name": fee.fullName || 'Unknown',
                                            "Class Name": fee.className || 'Unassigned',
                                            "Reg No": fee.registrationNumber || '-',
                                            Email: fee.email || '-',
                                            "Paid Date": fee.status === 'pending' ? '-' : formatDate(fee.paidDate),
                                            Amount: fee.amount || 0,
                                            Status: fee.status || '-',
                                            Notes: fee.notes || 'None',
                                            Actions: (
                                                <ButtonGroup size="sm">
                                                    {fee.status === 'pending' ? (
                                                        <Button
                                                            color="primary"
                                                            onClick={() => openAddModal({ ...fee, studentId: fee.studentId, _id: fee._id })}
                                                        >
                                                            <FaPlus /> Add
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            color="warning"
                                                            onClick={() => openEditModal({ ...fee, studentId: fee.studentId, _id: fee._id })}
                                                        >
                                                            <FaEdit /> Edit
                                                        </Button>
                                                    )}
                                                    <Button
                                                        color="secondary"
                                                        title="Print PDF"
                                                        onClick={() => handlePrintIndividual(fee)}
                                                    >
                                                        <FaPrint />
                                                    </Button>
                                                    <Button
                                                        color="info"
                                                        title="Share PDF"
                                                        onClick={() => handleShareIndividual(fee)}
                                                    >
                                                        <FaShareAlt />
                                                    </Button>
                                                </ButtonGroup>
                                            )
                                        }))
                                    ) : []}
                                />
                            )}
                            <CardFooter className="py-4">
                                <Pagination className="pagination justify-content-end mb-0" listClassName="justify-content-end mb-0">
                                    <ReactPaginate
                                        previousLabel={<i className="previous fa fa-chevron-left" aria-hidden="true"></i>}
                                        nextLabel={<i className="next fa fa-chevron-right" aria-hidden="true"></i>}
                                        breakLabel="..."
                                        pageCount={totalPages}
                                        onPageChange={handlePageChange}
                                        containerClassName="pagination justify-content-end mb-0"
                                        pageClassName="page-item"
                                        pageLinkClassName="page-link"
                                        activeClassName="active"
                                        previousClassName="page-item"
                                        nextClassName="page-item"
                                        previousLinkClassName="page-link"
                                        nextLinkClassName="page-link"
                                    />
                                </Pagination>
                            </CardFooter>
                        </Card>
                    </div>
                </Row>

                <Modal isOpen={modalOpen} toggle={() => { setModalOpen(false); setErrors({}); }}>
                    <ModalHeader toggle={() => { setModalOpen(false); setErrors({}); }}>Add New Fee for {selectedFee?.fullName || 'Student'}</ModalHeader>
                    <ModalBody>
                        <Form onSubmit={(e) => handleAddFee(e, selectedFee?.studentId)}>
                            <FormGroup>
                                <Label>Paid Date <span className="text-danger">*</span></Label>
                                <Input
                                    type="date"
                                    value={newFee.paidDate}
                                    onChange={(e) => setNewFee({ ...newFee, paidDate: e.target.value })}
                                    invalid={!!errors.paidDate}
                                />
                                {errors.paidDate && <div className="invalid-feedback">{errors.paidDate}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label>Month <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={newFee.month}
                                    onChange={(e) => setNewFee({ ...newFee, month: e.target.value })}
                                    invalid={!!errors.month}
                                    disabled
                                >
                                    <option value="">Select Month</option>
                                    {months.filter(m => m.value !== '').map(month => (
                                        <option key={month.value} value={month.value}>
                                            {month.label}
                                        </option>
                                    ))}
                                </Input>
                                {errors.month && <div className="invalid-feedback">{errors.month}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label>Year <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={newFee.year}
                                    onChange={(e) => setNewFee({ ...newFee, year: e.target.value })}
                                    invalid={!!errors.year}
                                    disabled
                                >
                                    <option value="">Select Year</option>
                                    {years.map(year => (
                                        <option key={year.value} value={year.value}>
                                            {year.label}
                                        </option>
                                    ))}
                                </Input>
                                {errors.year && <div className="invalid-feedback">{errors.year}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label>Amount <span className="text-danger">*</span></Label>
                                <Input
                                    type="number"
                                    placeholder="Enter amount"
                                    value={newFee.amount}
                                    onChange={(e) => setNewFee({ ...newFee, amount: e.target.value })}
                                    invalid={!!errors.amount}
                                    min="0"
                                />
                                {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                            </FormGroup>
                            {!newFee.isFullPay && (
                                <FormGroup>
                                    <Label>Pending Amount <span className="text-danger">*</span></Label>
                                    <Input
                                        type="number"
                                        placeholder="Enter pending amount"
                                        value={newFee.pending}
                                        onChange={(e) => setNewFee({ ...newFee, pending: e.target.value })}
                                        invalid={!!errors.pending}
                                        min="0"
                                    />
                                    {errors.pending && <div className="invalid-feedback">{errors.pending}</div>}
                                </FormGroup>
                            )}
                            <FormGroup className="d-flex align-items-center">
                                <Label className="mr-2">Full Pay <span className="text-danger">*</span></Label>
                                <Input
                                    type="checkbox"
                                    checked={newFee.isFullPay}
                                    onChange={(e) => setNewFee({ ...newFee, isFullPay: e.target.checked, pending: e.target.checked ? 0 : newFee.pending })}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Payment Method <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={newFee.isBank ? 'bank' : 'cash'}
                                    onChange={(e) => setNewFee({ ...newFee, isBank: e.target.value === 'bank' })}
                                    invalid={!!errors.isBank}
                                >
                                    <option value="">Select Payment Method</option>
                                    <option value="cash">Cash</option>
                                    <option value="bank">Bank Deposit</option>
                                </Input>
                                {errors.isBank && <div className="invalid-feedback">{errors.isBank}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label>Notes (optional)</Label>
                                <Input
                                    type="textarea"
                                    placeholder="Enter notes (optional)"
                                    value={newFee.notes}
                                    onChange={(e) => setNewFee({ ...newFee, notes: e.target.value })}
                                />
                            </FormGroup>
                            <Button color="primary" type="submit" disabled={Object.keys(errors).length > 0}>
                                Add Fee
                            </Button>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => { setModalOpen(false); setErrors({}); }}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={editModalOpen} toggle={() => { setEditModalOpen(false); setErrors({}); }}>
                    <ModalHeader toggle={() => { setEditModalOpen(false); setErrors({}); }}>Edit Fee for {selectedFee?.fullName || 'Student'}</ModalHeader>
                    <ModalBody>
                        <Form onSubmit={handleEditFee}>
                            <FormGroup>
                                <Label>Receipt Number</Label>
                                <Input
                                    type="text"
                                    placeholder="Enter receipt number"
                                    value={editFee?.receiptNumber || ''}
                                    readOnly
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Paid Date <span className="text-danger">*</span></Label>
                                <Input
                                    type="date"
                                    value={editFee?.paidDate || ''}
                                    onChange={(e) => setEditFee({ ...editFee, paidDate: e.target.value })}
                                    invalid={!!errors.paidDate}
                                />
                                {errors.paidDate && <div className="invalid-feedback">{errors.paidDate}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label>Month <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={editFee?.month || ''}
                                    onChange={(e) => setEditFee({ ...editFee, month: e.target.value })}
                                    invalid={!!errors.month}
                                >
                                    <option value="">Select Month</option>
                                    {months.filter(m => m.value !== '').map(month => (
                                        <option key={month.value} value={month.value}>
                                            {month.label}
                                        </option>
                                    ))}
                                </Input>
                                {errors.month && <div className="invalid-feedback">{errors.month}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label>Year <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={editFee?.year || ''}
                                    onChange={(e) => setEditFee({ ...editFee, year: e.target.value })}
                                    invalid={!!errors.year}
                                >
                                    <option value="">Select Year</option>
                                    {years.map(year => (
                                        <option key={year.value} value={year.value}>
                                            {year.label}
                                        </option>
                                    ))}
                                </Input>
                                {errors.year && <div className="invalid-feedback">{errors.year}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label>Amount <span className="text-danger">*</span></Label>
                                <Input
                                    type="number"
                                    placeholder="Enter amount"
                                    value={editFee?.amount || ''}
                                    onChange={(e) => setEditFee({ ...editFee, amount: e.target.value })}
                                    invalid={!!errors.amount}
                                    min="0"
                                />
                                {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                            </FormGroup>
                            {!editFee?.isFullPay && (
                                <FormGroup>
                                    <Label>Pending Amount <span className="text-danger">*</span></Label>
                                    <Input
                                        type="number"
                                        placeholder="Enter pending amount"
                                        value={editFee?.pending || ''}
                                        onChange={(e) => setEditFee({ ...editFee, pending: e.target.value })}
                                        invalid={!!errors.pending}
                                        min="0"
                                    />
                                    {errors.pending && <div className="invalid-feedback">{errors.pending}</div>}
                                </FormGroup>
                            )}
                            <FormGroup className="d-flex align-items-center">
                                <Label className="mr-2">Full Pay <span className="text-danger">*</span></Label>
                                <Input
                                    type="checkbox"
                                    checked={editFee?.isFullPay || false}
                                    onChange={(e) => setEditFee({ ...editFee, isFullPay: e.target.checked, pending: e.target.checked ? 0 : editFee.pending })}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Payment Method <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={editFee?.isBank ? 'bank' : 'cash'}
                                    onChange={(e) => setEditFee({ ...editFee, isBank: e.target.value === 'bank' })}
                                    invalid={!!errors.isBank}
                                >
                                    <option value="">Select Payment Method</option>
                                    <option value="cash">Cash</option>
                                    <option value="bank">Bank Deposit</option>
                                </Input>
                                {errors.isBank && <div className="invalid-feedback">{errors.isBank}</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label>Notes (optional)</Label>
                                <Input
                                    type="textarea"
                                    placeholder="Enter notes (optional)"
                                    value={editFee?.notes || ''}
                                    onChange={(e) => setEditFee({ ...editFee, notes: e.target.value })}
                                />
                            </FormGroup>
                            <Button color="primary" type="submit" disabled={Object.keys(errors).length > 0}>
                                Update Fee
                            </Button>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => { setEditModalOpen(false); setErrors({}); }}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
            <Footer />
        </>
    );
};

export default StudentMonthlyFee;